import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Bot, ArrowRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Homepage widget — shows today's most-recently-generated Daily Drop
 * (anonymised) so visitors see the system producing fresh universe-expansion
 * art every day. Polls every 90s for new drops.
 */
export default function LatestDropWidget() {
  const [drop, setDrop] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchDrop = async () => {
      try {
        const { data } = await axios.get(`${API}/ai/daily-drops/latest`);
        if (!cancelled) {
          setDrop(data.drop || null);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) setLoading(false);
      }
    };
    fetchDrop();
    const id = setInterval(fetchDrop, 90_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (loading || !drop) return null;

  return (
    <section className="py-12 md:py-16" data-testid="latest-drop-widget">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10 items-center rounded-3xl border border-[#D946EF]/20 bg-gradient-to-br from-[#0F1018] to-[#0a0a12] overflow-hidden">
          {/* Image */}
          <div className="relative aspect-[4/3] md:aspect-auto md:h-full overflow-hidden">
            <img
              src={drop.image_base64}
              alt={drop.theme || "Today's Bullpug Drop"}
              loading="lazy"
              className="w-full h-full object-cover"
            />
            {/* Soft right-edge fade so the image bleeds into the copy on desktop */}
            <div className="hidden md:block absolute inset-y-0 right-0 w-20 bg-gradient-to-r from-transparent to-[#0F1018] pointer-events-none" />
          </div>

          {/* Copy */}
          <div className="p-6 md:p-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F5D300]/10 border border-[#F5D300]/30 text-[#F5D300] text-[10px] font-bold uppercase tracking-[0.25em] mb-4">
              <Sparkles className="w-3 h-3" />
              {drop.pinned ? "Today's Drop · Curator Pick" : "Today's Drop · Fresh from the Chatbot"}
            </div>

            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter text-white mb-3 leading-[1.05]"
              style={{ fontFamily: "Orbitron, sans-serif" }}
            >
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D946EF] via-[#00FFA3] to-[#FFD700]">
                {drop.pinned_title || drop.theme || "Untitled"}
              </span>
            </h2>

            <p className="text-sm md:text-base text-slate-300 leading-relaxed max-w-md mb-5">
              {drop.pinned_description || drop.scene}
            </p>

            <p className="text-xs text-slate-500 leading-relaxed mb-6 max-w-md">
              {drop.pinned
                ? <>Handpicked by the curator · {drop.date_utc}.</>
                : <>A new Daily Drop is generated for every visitor who opens the chatbot. Universe expansion · {drop.date_utc} · gone at midnight UTC.</>
              }
            </p>

            <div className="flex flex-wrap items-center gap-3">
              <Link to="/archive?tab=drops" data-testid="latest-drop-cta">
                <button className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#D946EF] hover:bg-[#D946EF]/90 text-black font-bold text-xs uppercase tracking-wider transition-colors shadow-[0_0_24px_rgba(217,70,239,0.30)]">
                  <Bot className="w-4 h-4" />
                  Claim your drop
                  <ArrowRight className="w-3 h-3" />
                </button>
              </Link>
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                Generated by Nano Banana
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
